import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { Container } from "../../components/Container";
import { Title } from "../../components/Title";
import { SubmitButton, BackButton } from "../../components/Button";
import { TextInput } from "../../components/TextInput";
import { SelectBox } from "../../components/SelectBox";
import UserUtils from "../../util/UserUtils";
import { getOrderById } from "../../api/AWSAPI";
import { OrderSidebar } from "../../components/Sidebar";
import {
  ORDER_STATUS,
  ORDER_STATUS_CONFIG,
  PAYMENT_METHODS,
  DATE_FORMATS,
} from "../../constants/orderConstants";
import ApiClient from "../../api/client";
import APIUtils from "../../api/APIUtils";

// Helper component for order status badge
const OrderStatusBadge = ({ status }) => {
  const config =
    ORDER_STATUS_CONFIG[status] ||
    ORDER_STATUS_CONFIG[ORDER_STATUS.AWAITING_PAYMENT];

  return (
    <span
      className={config.className}
      style={{ marginTop: -20, marginRight: 20 }}
    >
      {config.text}
    </span>
  );
};

function OrderFundsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiClient = new ApiClient(process.env.REACT_APP_AWS_URL);
  const [isUpdating, setIsUpdating] = useState(false);

  // State consolidation
  const [orderData, setOrderData] = useState({
    orderNumber: "",
    orderDate: "",
    clientName: "",
    orderStatus: 0,
  });

  const [formData, setFormData] = useState({
    paymentMethod: "",
    fundsReceived: "",
    transRef: "",
  });

  const [userData, setUserData] = useState({
    confirmedByName: "",
  });

  const [formErrors, setFormErrors] = useState({
    paymentMethod: "",
    fundsReceived: "",
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load order data
  const loadOrder = useCallback(() => {
    getOrderById(id, (data) => {
      if (data?.success && data.order.length > 0) {
        const order = data.order[0];
        setOrderData((prev) => ({
          ...prev,
          orderNumber: order.orderNumber,
          orderDate: order.orderDate,
          clientName: order.contactCompany,
          orderStatus: order.orderStatus,
          orderContactEmail: order.contactEmail,
        }));
      }
    });
  }, [id]);

  // Load user data
  const loadUserData = useCallback(() => {
    UserUtils.getUserData((data) => {
      if (data) {
        setUserData({
          confirmedByName: `${data.name || ""}`.trim(),
        });
      } else {
        navigate("/");
      }
    });
  }, [navigate]);

  // Load all data on mount
  useEffect(() => {
    if (!id) return;

    loadOrder();
    loadUserData();
    setIsLoading(false);
  }, [id, loadOrder, loadUserData]);

  // Update form field and clear error
  const updateFormField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.transRef || formData.transRef.trim() === "") {
      errors.transRef = "Please enter the transaction reference";
    }

    if (!formData.paymentMethod || formData.paymentMethod === "0") {
      errors.paymentMethod = "Please select the payment method";
    }

    const date = moment(formData.fundsReceived);
    if (!formData.fundsReceived || !date.isValid() || date.isAfter(moment())) {
      errors.fundsReceived = "Please select a valid date";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleConfirm = async () => {
    setIsUpdating(true);
    if (!validateForm()) {
      setIsUpdating(false);
      return;
    }

    const payload = {
      orderStatus: ORDER_STATUS.PAYMENT_RECEIVED,
      paymentConfirmedBy: userData.confirmedByName,
      paymentMethod: formData.paymentMethod,
      paymentReference: formData.transRef,
      paymentReceivedDate: moment(formData.fundsReceived).toISOString(),
      confirmedDateTime: moment().toISOString(),
    };

    try {
      const response = await apiClient.put(
        `/${APIUtils.GATE_API}/NAGCO/order/confirmpayment/${id}`,
        payload,
        {
          headers: await APIUtils.getAuthorizationHeader(),
        }
      );

      if (!response.success) {
        throw new Error(
          `Failed to update order: ${response.data?.errorTxt || response.message || "Unknown error"}`
        );
      }

      navigate(`/orders/${id}`);
    } catch (error) {
      console.error("Error confirming payment:", error);
      setError("An error occurred while confirming funds. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-sm-12 content">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-sm-12 content">
            <BackButton title="Back" url="/orders" />
            <p className="text-danger">{error}</p>
            <p>If this issue persists, please contact support.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row">
        <div className="col-sm-12 content">
          <BackButton title="Back" url={`/orders/${id}`} />
          <Title
            title={`${orderData.orderNumber} - ${orderData.clientName}`}
          />
          <div>
            <OrderStatusBadge status={orderData.orderStatus} /> Created:{" "}
            {moment(orderData.orderDate).format(DATE_FORMATS.ORDER_DATE)}
          </div>
        </div>
      </div>
      <div className="row row-eq-height">
        <div className="col-sm-8 content">
          <Container>
            <div className="row">
              <div className="col-sm-12">
                <h4 style={{ fontWeight: "bold", color: "#333" }}>
                  Confirm Funds Received
                </h4>
                <div className="row">
                  <div className="col-sm-6">
                    <SelectBox
                      name="paymentMethod"
                      label="Payment Method"
                      placeholder="Select..."
                      value={formData.paymentMethod}
                      maxLength={45}
                      asterix={true}
                      options={PAYMENT_METHODS}
                      onChange={(name, value) =>
                        updateFormField("paymentMethod", value)
                      }
                      error={formErrors.paymentMethod}
                    />
                  </div>
                  <div className="col-sm-6">
                    <TextInput
                      name="confirmedByName"
                      type="text"
                      label="Confirmed By"
                      value={userData.confirmedByName}
                      disabled={true}
                      maxLength={45}
                      asterix={true}
                      onChange={() => { }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-6">
                    <TextInput
                      name="fundsReceived"
                      type="date"
                      label="Date Received"
                      value={formData.fundsReceived}
                      maxLength={45}
                      asterix={true}
                      onChange={(name, value) =>
                        updateFormField("fundsReceived", value)
                      }
                      error={formErrors.fundsReceived}
                    />
                  </div>
                  <div className="col-sm-6">
                    <TextInput
                      name="transRef"
                      type="text"
                      label="Transaction Reference"
                      asterix={true}
                      placeholder="Enter reference..."
                      value={formData.transRef}
                      maxLength={45}
                      onChange={(name, value) =>
                        updateFormField("transRef", value.trim())
                      }
                      error={formErrors.transRef}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Container>

          <div className="row">
            <div className="col-sm-12 text-right">
              {isUpdating ? (
                <SubmitButton className="btn" text="Confirming..." disabled />
              ) : (
                <SubmitButton
                  text="Confirm Funds Received"
                  className="btn"
                  onClick={handleConfirm}
                  disabled={orderData.orderStatus === ORDER_STATUS.PAYMENT_RECEIVED}
                />
              )}
            </div>
          </div>
        </div>
        <div className="col-sm-4 content">
          <OrderSidebar id={id} />
        </div>
      </div>
    </div>
  );
}

export default OrderFundsPage;
